import {LoaderFunctionArgs} from 'react-router-dom';
import {getRootStore} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {getDashboardLanguage} from '../helper/general.helper';
import {resolveUserAndAccount} from '../helper/auth-resolve.helper';
import {IWifiAnalyzerHistoryStore, WifiAnalyzerHistoryStore, SearchCriteria} from './wifi-analyzer-history.stores';

const trace = getAppTracer('wifi-analyzer-history.loader');

type SessionData = {
    accountId?: string;
    startDateValue?: Date | string;
    endDateValue?: Date | string;
    customerId?: string;
    preDataCollectionCode?: string;
    customerNumber?: string;
    customerEmail?: string;
};

function getSearchCriteria(
    urlParams: {
        customerRef?: string;
        startDate?: string;
        endDate?: string;
        roomId?: string;
        customerNumber?: string;
        customerEmail?: string;
        scanId?: string;
        preDataCollectionCode?: string;
    },
    browserUtilsService: {getFromSessionStorage: (key: string) => unknown}
): SearchCriteria {
    if (urlParams.roomId) {
        return {roomId: urlParams.roomId};
    }

    const urlParamsEmpty = Object.values(urlParams).every((val) => val === '' || val === undefined);
    const sessionData = browserUtilsService.getFromSessionStorage('wifiAnalyzerHistory') as SessionData | undefined;

    if (urlParamsEmpty && sessionData) {
        return {
            customerRef: sessionData.customerId,
            preDataCollectionCode: sessionData.preDataCollectionCode,
            customerNumber: sessionData.customerNumber,
            customerEmail: sessionData.customerEmail,
            startDate:
                sessionData.startDateValue instanceof Date
                    ? sessionData.startDateValue.toString()
                    : (sessionData.startDateValue as string),
            endDate:
                sessionData.endDateValue instanceof Date
                    ? sessionData.endDateValue.toString()
                    : (sessionData.endDateValue as string)
        };
    }

    return {
        scanId: urlParams.scanId,
        endDate: urlParams.endDate,
        startDate: urlParams.startDate,
        customerRef: urlParams.customerRef,
        customerEmail: urlParams.customerEmail,
        customerNumber: urlParams.customerNumber,
        preDataCollectionCode: urlParams.preDataCollectionCode
    };
}

export async function wifiAnalyzerHistoryRouteLoader(
    args: LoaderFunctionArgs
): Promise<IWifiAnalyzerHistoryStore | void> {
    trace.info('Starting');

    const rootStore = getRootStore();
    const browserUtils = rootStore.browserUtilsService;

    const urlParams = {
        customerRef: browserUtils.getParamValue('r') ?? undefined,
        startDate: browserUtils.getParamValue('startDate') ?? undefined,
        endDate: browserUtils.getParamValue('endDate') ?? undefined,
        roomId: browserUtils.getParamValue('roomId') ?? undefined,
        customerNumber: browserUtils.getParamValue('p'),
        customerEmail: browserUtils.getParamValue('e'),
        preDataCollectionCode: browserUtils.getParamValue('prg'),
        scanId: browserUtils.getParamValue('scanId')
    };

    const isEmbedded =
        browserUtils.getParamValue('emb') === 'true' || browserUtils.getParamValue('is')?.toLowerCase() === 'vjhistory';

    const redirectParams = Object.fromEntries(Object.entries(urlParams));

    const {accountData} = await resolveUserAndAccount({redirectParams});

    const localizationService = rootStore.localizationService;
    if (!localizationService.isInitialized()) {
        const accountLanguage = getDashboardLanguage(accountData);
        localizationService.setAccountData(accountData._id, accountLanguage);
        await localizationService.init();
    }

    const searchCriteria = getSearchCriteria(urlParams, rootStore.browserUtilsService);

    const store = new WifiAnalyzerHistoryStore(accountData, searchCriteria, isEmbedded);

    trace.info('Completed successfully');
    return store;
}
